import React from 'react';
import { Info } from 'lucide-react';
import MuiTooltip from '@mui/material/Tooltip';
import { useNamePrecedenceConfig } from '../../../services/api/domains/products/hooks';
import { NamePrecedenceStep } from '../../../services/api/domains/products/types';
import { GRAY_400 } from 'theme/grayScale';

const FALLBACK_TOOLTIP_TEXT =
  'Product Name is resolved using a configured precedence order.';

/**
 * Pure content builder, exported separately so tests can cover every status
 * (idle/loading/success/error) without mocking the fetch hook each time.
 * Loading/idle/error all fall back to the same static message (AD Requirement
 * 1) rather than showing an empty or partially-built tooltip.
 */
export function buildPrecedenceTooltipContent(
  status: 'idle' | 'loading' | 'success' | 'error',
  steps: NamePrecedenceStep[] | undefined
): React.ReactNode {
  if (status !== 'success' || !steps || steps.length === 0) {
    return FALLBACK_TOOLTIP_TEXT;
  }

  return (
    <div>
      <div>Product Name is resolved in order:</div>
      <ol style={{ margin: '4px 0 0', paddingLeft: '18px' }}>
        {steps.map(s => (
          <li key={s.step}>{s.label}</li>
        ))}
      </ol>
    </div>
  );
}

interface PrecedenceOrderTooltipProps {
  /** Icon size in px — callers place this inline next to differently-sized text. */
  size?: number;
}

/**
 * Info icon + hover tooltip showing the live dbo.Name_Precedence_Config order.
 * Shared by SynonymsRadioField's VIEW-mode label row and EDIT-mode hint line
 * so the two surfaces can never drift apart — one fetch, one content builder,
 * two call sites.
 */
const PrecedenceOrderTooltip: React.FC<PrecedenceOrderTooltipProps> = ({
  size = 13,
}) => {
  const result = useNamePrecedenceConfig();
  const content = buildPrecedenceTooltipContent(
    result.status,
    result.status === 'success' ? result.data : undefined
  );

  return (
    <MuiTooltip
      title={content}
      placement="right"
      arrow
      slotProps={{ popper: { style: { zIndex: 10001 } } }}>
      <button
        type="button"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          color: GRAY_400,
          cursor: 'help',
          background: 'none',
          border: 'none',
          padding: 0,
          margin: 0,
        }}
        aria-label="How is Product Name resolved?">
        <Info size={size} />
      </button>
    </MuiTooltip>
  );
};

export default PrecedenceOrderTooltip;
